import type { ExploitDbExtraction } from '@intelhub/shared'
import { ExploitDbReaderPane } from './ExploitDbReaderPane'
import type { FullTextReason, FullTextStatus } from '../../types/story'

/**
 * Inline reader pane for an article's cleaned full-text body.
 *
 * Used by:
 *   - `SourcesModal` (multi-source aggregator, accessed via "View N sources")
 *   - `StoryDetailPage` (per-source row, primary discovery surface)
 *
 * KAN-48 — status-aware. The optional `fullTextStatus` is the authoritative
 * signal for post-feature rows. When it is null (pre-feature rows pre-dating
 * the migration backfill), the pane falls back to the legacy
 * `fullTextMeta`/`fullText` precedence so existing surfaces keep rendering
 * correctly without an API contract migration.
 *
 * The pane MUST NOT expose raw reason codes, HTTP status, stack traces, or
 * the word "denylist" to normal readers. Safe supplements only.
 */

const UNAVAILABLE_BASE_MESSAGE =
  'Full text is not available in CyberFind for this source. Read the original article on the source site.'

function reasonSupplement(reason: FullTextReason | null | undefined): string | null {
  switch (reason) {
    case 'denylisted':
    case 'link_only_source':
      return 'CyberFind is keeping this source link-only under the source policy.'
    case 'blocked_or_challenge':
      return 'The source did not provide a normal public article response.'
    case 'paywall_or_registration':
      return 'The source appears to require registration or subscription access.'
    case 'timeout':
      return 'The public fetch timed out.'
    case 'non_2xx':
      return 'The source returned an error response.'
    case 'content_too_short':
    case 'readability_empty':
      return 'CyberFind could not extract enough readable article text.'
    // 'disabled', 'standard_fetch_only', 'extractor_error', 'unknown', 'none'
    // fall through to base message only — no reason-revealing supplement.
    default:
      return null
  }
}

function UnavailablePanel({
  reason,
  url,
  onContinueToSource,
}: {
  reason: FullTextReason | null | undefined
  url: string
  onContinueToSource: (url: string) => void
}) {
  const supplement = reasonSupplement(reason)
  return (
    <div
      className="rounded-md border bg-muted/30 p-4"
      data-testid="article-reader-pane"
      data-state="unavailable"
    >
      <p className="text-sm text-muted-foreground">{UNAVAILABLE_BASE_MESSAGE}</p>
      {supplement && (
        <p
          className="mt-2 text-sm text-muted-foreground"
          data-testid="article-reader-pane-supplement"
        >
          {supplement}
        </p>
      )}
      <p className="mt-3 text-xs">
        <button
          type="button"
          className="text-primary underline-offset-2 hover:underline"
          onClick={() => onContinueToSource(url)}
        >
          Open in source ↗
        </button>
      </p>
    </div>
  )
}

export function ArticleReaderPane({
  fullText,
  fullTextMeta,
  fullTextStatus,
  fullTextReason,
  publisher,
  url,
  onContinueToSource,
}: {
  fullText: string | null
  fullTextMeta?: ExploitDbExtraction | null
  fullTextStatus?: FullTextStatus | null
  fullTextReason?: FullTextReason | null
  publisher: string
  url: string
  onContinueToSource: (url: string) => void
}) {
  // KAN-48 — status-aware precedence. When `fullTextStatus` is set, it is the
  // authoritative signal: only `available` may render a reader payload. Old
  // rows (status null) preserve the legacy fullTextMeta → fullText → fallback
  // chain.
  if (fullTextStatus != null) {
    if (fullTextStatus === 'available') {
      if (fullTextMeta) {
        return (
          <ExploitDbReaderPane
            meta={fullTextMeta}
            url={url}
            onContinueToSource={onContinueToSource}
          />
        )
      }
      if (fullText) {
        return (
          <div
            className="rounded-md border bg-muted/30 p-4"
            data-testid="article-reader-pane"
            data-state="available"
          >
            <p className="mb-3 text-xs text-muted-foreground">
              Cleaned reader view — no tracker scripts. Source: {publisher}.
            </p>
            <div className="prose prose-sm dark:prose-invert max-w-none overflow-y-auto max-h-96 text-sm whitespace-pre-wrap">
              {fullText}
            </div>
            <p className="mt-3 text-xs">
              <button
                type="button"
                className="text-primary underline-offset-2 hover:underline"
                onClick={() => onContinueToSource(url)}
              >
                Continue reading on source ↗
              </button>
            </p>
          </div>
        )
      }
      // Invariant violation: status claims available but neither payload is
      // present. Render the safe unavailable panel so the reader is never
      // shown a blank or broken state.
      return (
        <UnavailablePanel
          reason={null}
          url={url}
          onContinueToSource={onContinueToSource}
        />
      )
    }

    // status is skipped / unavailable / failed
    return (
      <UnavailablePanel
        reason={fullTextReason ?? null}
        url={url}
        onContinueToSource={onContinueToSource}
      />
    )
  }

  // Pre-feature legacy fallback — status is null. Preserve historical
  // behaviour driven by fullTextMeta/fullText.
  if (fullTextMeta) {
    return (
      <ExploitDbReaderPane
        meta={fullTextMeta}
        url={url}
        onContinueToSource={onContinueToSource}
      />
    )
  }

  return (
    <div
      className="rounded-md border bg-muted/30 p-4"
      data-testid="article-reader-pane"
    >
      {fullText ? (
        <>
          <p className="mb-3 text-xs text-muted-foreground">
            Cleaned reader view — no tracker scripts. Source: {publisher}.
          </p>
          <div className="prose prose-sm dark:prose-invert max-w-none overflow-y-auto max-h-96 text-sm whitespace-pre-wrap">
            {fullText}
          </div>
          <p className="mt-3 text-xs">
            <button
              type="button"
              className="text-primary underline-offset-2 hover:underline"
              onClick={() => onContinueToSource(url)}
            >
              Continue reading on source ↗
            </button>
          </p>
        </>
      ) : (
        <p className="text-sm text-muted-foreground">
          Content not retrievable. Please{' '}
          <button
            type="button"
            className="text-primary underline-offset-2 hover:underline"
            onClick={() => onContinueToSource(url)}
          >
            continue to source
          </button>{' '}
          to read further.
        </p>
      )}
    </div>
  )
}
